import React, { useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import {
  AccountBalanceOutlined,
  AddCircleOutline,
  BadgeOutlined,
  CakeOutlined,
  CalendarMonthOutlined,
  CurrencyRupeeOutlined,
  EditOutlined,
  EventOutlined,
  FileUploadOutlined,
  HowToRegOutlined,
  NotesOutlined,
  PersonOffOutlined,
  SavingsOutlined,
  TagOutlined,
} from "@mui/icons-material";
import { Loader } from "components/Loader";
import { GlobalSidePanel, PanelHeaderIconButton } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PanelActionFooter, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { showToast } from "services/utils/Status";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { Required, SimpleTable, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { MilestoneStepper, PlainInput, RecordHeaderCard, SwitchLine, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { MEMBER_STATUS_COLORS, formatDay, formatMonthKey, monthOptions, pfAmounts } from "./pfMembersData";
import { openPfFile } from "./pfMembersApi";

// PF member drawers: Member Details (P5), Extra PF Contribution (P6) and
// PF Opt-out (P6b). No approval step: Save applies the change (B4.3). The
// server checks every save again; its field errors show on the fields.

const { indigo, pink, blue, green, orange, purple } = detailAccentPalette;
const RED = "#DC2626";
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const FILE_TYPES = [".pdf", ".jpg", ".jpeg", ".png"];
const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const memberMeta = (member) => [member.emp_id, member.department, member.location].filter(Boolean).join(" · ");

export const fileError = (file) => {
  if (!file) return "";
  if (!FILE_TYPES.some((type) => file.name.toLowerCase().endsWith(type))) return "Upload a PDF, JPG or PNG file";
  if (file.size > MAX_FILE_BYTES) return "The file is larger than 5 MB";
  return "";
};

// Server field errors -> { fieldKey: message }; anything else -> general.
const splitErrors = (error, keys) => {
  const fields = {};
  const general = [];
  Object.entries(error?.fieldErrors || {}).forEach(([key, message]) => {
    if (keys[key]) fields[keys[key]] = message;
    else general.push(message);
  });
  if (!Object.keys(fields).length && !general.length) general.push(error?.message || "Could not save. Please try again.");
  return { fields, general: general.join(" ") };
};

// Small "upload a file" button holding the picked File (sent with the
// save). PDF / JPG / PNG up to 5 MB.
export const FilePick = ({ label, hint, value, onChange, error }) => {
  const inputRef = useRef(null);
  return (
    <Mui.Box>
      <Mui.Box
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") inputRef.current?.click(); }}
        sx={{
          border: `1px dashed ${error ? RED : designColors.teal}`,
          borderRadius: 2,
          backgroundColor: designColors.tealTint,
          px: 2,
          py: 1.5,
          textAlign: "center",
          cursor: "pointer",
        }}
      >
        <input
          ref={inputRef}
          hidden
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          data-testid={`${label}-input`}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onChange(file);
            event.target.value = "";
          }}
        />
        <Mui.Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ color: designColors.tealDark }}>
          <FileUploadOutlined sx={{ fontSize: 18 }} />
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold, color: designColors.tealDark }}>
            {value?.name || label}
          </Mui.Typography>
        </Mui.Stack>
        <Mui.Typography sx={{ ...designType.captionText, mt: 0.25 }}>{value ? "Click to change" : hint}</Mui.Typography>
      </Mui.Box>
      {error ? <Mui.Typography sx={{ ...designType.captionText, color: RED, mt: 0.5 }}>{error}</Mui.Typography> : null}
    </Mui.Box>
  );
};

// A stored letter / Form 11: opens through the API (files need the token).
const FileLink = ({ domain, url, name }) => {
  const [busy, setBusy] = useState(false);
  if (!url) return name || "Not attached";
  return (
    <Mui.Link
      component="button"
      type="button"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await openPfFile({ domain, url });
        } catch (error) {
          showToast("error", error.message || "Could not open the file.");
        } finally {
          setBusy(false);
        }
      }}
      sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold, textAlign: "left" }}
    >
      {name || "View file"}
    </Mui.Link>
  );
};

// ---------------- Member Details (P5) ----------------

export const PfMemberDetailsPanel = ({ open, loading, error, onRetry, member, settings, domain, onClose, onEditExtra, onOptOut, onEndOptOut }) => {
  if (!open && !member) return null;
  const status = String(member?.status || "").toLowerCase().replace(/[s_-]+/g, " ").trim();
  const optedOut = status === "opted out" || status === "opt out";
  const isMember = status === "member" || status === "pf member";
  // Header actions follow the member's status:
  //   Member    → Opt out of PF + Edit Extra PF
  //   Opted out → End opt-out
  //   Not in PF → none
  // A drawer that can't be saved as things stand says why in a banner.
  let headerActions = null;
  if (member && isMember) {
    headerActions = (
      <>
        <PanelHeaderIconButton title="Opt out of PF" onClick={() => onOptOut(member)} sx={{ color: "#B54708" }}>
          <PersonOffOutlined />
        </PanelHeaderIconButton>
        <PanelHeaderIconButton title="Edit Extra PF" onClick={() => onEditExtra(member)} sx={{ color: designColors.tealDark }}>
          <EditOutlined />
        </PanelHeaderIconButton>
      </>
    );
  } else if (member && optedOut) {
    headerActions = (
      <PanelHeaderIconButton title="End opt-out" onClick={() => onEndOptOut(member)} sx={{ color: designColors.tealDark }}>
        <HowToRegOutlined />
      </PanelHeaderIconButton>
    );
  }

  let body;
  if (error && !member) {
    body = (
      <RequestInfoBanner
        tone="danger"
        title="Couldn't load the PF details."
        body={error}
        action={<SecondaryButton onClick={onRetry} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
      />
    );
  } else if (loading || !member) {
    body = (
      <Mui.Box role="status" aria-label="Loading PF details">
        <Loader info="Loading..." minHeight="40vh" />
      </Mui.Box>
    );
  } else {
    const code = member.pf_code;
    const stopAge = settings?.stopAge;
    const stopsOn = member.pension_stops_month ? moment(member.pension_stops_month) : null;
    const hasExtra = member.full_salary || member.extra_amount;
    const latestExtra = member.extra_history?.[0];
    const pensionValue =
      member.pension === "YES" && stopsOn && stopAge ? `Yes · stops at age ${stopAge} (${stopsOn.format("MMM YYYY")})` : member.pension_label;

    body = (
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar={initialsOf(member.name)}
          title={member.name}
          chip={<RequestStatusChip status={isMember ? "PF member" : member.status} colorCode={MEMBER_STATUS_COLORS[member.status]} />}
          meta={memberMeta(member)}
          tone={optedOut ? "warning" : "teal"}
        />

        <RequestDetailsSection
          title="MEMBERSHIP"
          fields={[
            { icon: CalendarMonthOutlined, label: "PF joining date", value: member.joined_pf ? formatDay(member.joined_pf) : "—", accent: indigo },
            {
              icon: TagOutlined,
              label: "UAN",
              value: member.uan || (isMember ? "To be generated" : "—"),
              helper: !member.uan && isMember ? "Create it on the EPFO portal and add it with Import UAN" : undefined,
              accent: green,
            },
            {
              icon: AccountBalanceOutlined,
              label: "PF code",
              value: code
                ? `${code.code} · ${code.type === "MAIN" ? "Main" : "Extension"}${member.location ? ` (from location ${member.location})` : ""}`
                : `Not linked${member.location ? ` (location ${member.location})` : ""}`,
              accent: purple,
              multiline: true,
            },
            {
              icon: CakeOutlined,
              label: "Date of birth",
              value: member.dob ? `${formatDay(member.dob)} (age ${member.age})` : "Missing",
              helper: member.dob ? undefined : "Add it in People › Employee profile › Personal",
              accent: blue,
            },
            {
              icon: SavingsOutlined,
              label: "Pension (EPS)",
              value: pensionValue,
              helper: member.eps_source === "IMPORT" ? "From Import UAN" : "Decided when PF started",
              accent: orange,
            },
          ]}
        />

        {optedOut ? (
          <RequestDetailsSection
            title="OPT-OUT"
            fields={[
              { icon: EventOutlined, label: "Effective from", value: member.opt_out_from_label || "—", accent: orange },
              { icon: NotesOutlined, label: "Reason", value: member.opt_out_reason || "—", accent: pink, multiline: true },
              {
                icon: BadgeOutlined,
                label: "Form 11",
                value: <FileLink domain={domain} url={member.opt_out_form11_url} name={member.opt_out_form11_name} />,
                accent: indigo,
              },
            ]}
          />
        ) : isMember ? (
          <RequestDetailsSection
            title="EXTRA PF CONTRIBUTION"
            fields={
              hasExtra
                ? [
                    {
                      icon: CurrencyRupeeOutlined,
                      label: "PF on full salary",
                      value: member.full_salary
                        ? `Yes — PF on ${money(member.pf_wage)} instead of ${money(Math.min(member.pf_wage, settings?.ceiling ?? member.pf_wage))}`
                        : "No",
                      accent: purple,
                    },
                    { icon: AddCircleOutline, label: "Extra amount every month", value: member.extra_amount ? money(member.extra_amount) : "None", accent: pink },
                    {
                      icon: CalendarMonthOutlined,
                      label: "From",
                      value: `${formatMonthKey(member.extra_from)}${member.extra_set_by ? ` · set by ${member.extra_set_by}` : ""}`,
                      accent: blue,
                    },
                    ...(latestExtra?.letter_url
                      ? [
                          {
                            icon: BadgeOutlined,
                            label: "Signed letter",
                            value: <FileLink domain={domain} url={latestExtra.letter_url} name={latestExtra.letter_name} />,
                            accent: indigo,
                          },
                        ]
                      : []),
                  ]
                : [{ icon: CurrencyRupeeOutlined, label: "Extra PF", value: "None — normal PF only", accent: purple }]
            }
          />
        ) : null}

        {isMember ? (
          <MilestoneStepper
            title="History"
            steps={[
              { label: "Joined PF", date: member.joined_pf ? formatDay(member.joined_pf) : "", done: Boolean(member.joined_pf && moment(member.joined_pf).isSameOrBefore(moment(), "day")) },
              ...(hasExtra ? [{ label: "Extra PF from", date: formatMonthKey(member.extra_from), done: moment(`${member.extra_from}-01`).isSameOrBefore(moment(), "day") }] : []),
              { label: "Pension stops", date: stopsOn ? stopsOn.format("MMM YYYY") : "", done: Boolean(stopsOn && stopsOn.isSameOrBefore(moment(), "day")) },
            ]}
          />
        ) : null}
      </Mui.Stack>
    );
  }

  return (
    <GlobalSidePanel open={open} onClose={onClose} title="Employee PF Details" subtitle="PF details" bodySx={panelBodySx} headerActions={headerActions}>
      {body}
    </GlobalSidePanel>
  );
};

// ---------------- Extra PF Contribution (P6) ----------------

const EXTRA_ERROR_KEYS = { extra_amount: "extra", from_month: "from", full_salary: "fullSalary", letter: "letter" };

// Used only to open the Extra PF drawer when the PF settings didn't load:
// statutory PF ceiling, no components (amounts can't be worked out).
const FALLBACK_SETTINGS = { ceiling: 15000, epsCap: 15000, stopAge: 58, basis: "", components: [], vpfEnabled: true, vpfMaxPct: 100, actualAllowed: true, optOutAllowed: true };

// Helper line under a switch, lined up with the switch label.
const switchHelpSx = { ...designType.captionText, color: designColors.slate500, ml: "54px", mt: -0.5 };

export const PfExtraPanel = ({ open, member, settings: loadedSettings, onClose, onSave }) => {
  const settings = loadedSettings || FALLBACK_SETTINGS;
  const [form, setForm] = useState({ fullSalary: false, hasExtra: false, extra: "", from: "", letter: null });
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [server, setServer] = useState({ fields: {}, general: "" });

  useEffect(() => {
    if (!open || !member) return;
    const months = monthOptions();
    setForm({
      fullSalary: Boolean(member.full_salary),
      hasExtra: Boolean(member.extra_amount),
      extra: member.extra_amount ? String(member.extra_amount) : "",
      // Past months never change: a new setting starts this month or later.
      from: member.extra_from && member.extra_from >= months[0].value ? member.extra_from : months[1].value,
      letter: null,
    });
    setShowErrors(false);
    setSaving(false);
    setServer({ fields: {}, general: "" });
  }, [open, member]);

  if (!member) return null;
  const set = (key) => (value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setServer((current) => ({ ...current, fields: { ...current.fields, [key]: undefined } }));
  };
  const notices = [
    !loadedSettings ? "PF settings are not loaded (Payroll › Payroll Settings › Statutory › PF), so the monthly effect can't be worked out and Save may be refused." : "",
    member.status !== "Member" ? `${member.name} is not a PF member (${member.status}). Extra PF applies only to PF members.` : "",
  ].filter(Boolean);
  const extraValue = form.hasExtra && settings.vpfEnabled ? Number(form.extra) || 0 : 0;
  const now = pfAmounts(member.pf_wage, { fullSalary: member.full_salary, extra: member.extra_amount }, settings);
  const after = pfAmounts(member.pf_wage, { fullSalary: form.fullSalary, extra: extraValue }, settings);
  const maxExtra = Math.floor((after.base * settings.vpfMaxPct) / 100);
  const difference = after.cut - now.cut;

  const errors = {};
  if (form.hasExtra && settings.vpfEnabled && !(extraValue > 0)) errors.extra = "Enter the extra amount";
  else if (form.hasExtra && settings.vpfEnabled && !Number.isInteger(extraValue)) errors.extra = "Enter a whole rupee amount";
  else if (form.hasExtra && settings.vpfEnabled && extraValue > maxExtra) errors.extra = `Up to ${settings.vpfMaxPct}% of PF wage (${money(maxExtra)})`;
  if (!form.from) errors.from = "Pick the month it starts";
  if (fileError(form.letter)) errors.letter = fileError(form.letter);
  const error = (key) => server.fields[key] || (showErrors ? errors[key] : undefined);

  const save = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length || saving) return;
    setSaving(true);
    setServer({ fields: {}, general: "" });
    try {
      await onSave({ fullSalary: form.fullSalary, extra: extraValue, from: form.from, letter: form.letter });
    } catch (saveError) {
      setServer(splitErrors(saveError, EXTRA_ERROR_KEYS));
    } finally {
      setSaving(false);
    }
  };

  const fromOptions = monthOptions();
  const aboveCeiling = member.pf_wage > settings.ceiling;

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Extra PF Contribution"
      subtitle={`${member.name} · ${member.emp_id} · Basic + DA ${money(member.pf_wage)}`}
      bodySx={panelBodySx}
      footer={<PanelActionFooter onCancel={onClose} primaryLabel={saving ? "Saving..." : "Save"} primaryProps={{ onClick: save, disabled: saving }} />}
    >
      <Mui.Stack spacing={2}>
        {server.general ? <RequestInfoBanner tone="danger" title="Couldn't save" body={server.general} /> : null}
        {notices.map((notice) => <RequestInfoBanner key={notice} tone="warning" body={notice} />)}
        <RequestInfoBanner body={`Normally PF is cut on salary up to ${money(settings.ceiling)}. The employee can ask to pay more.`} />

        <FormSection title="What does the employee want?" info="Turn on what the employee asked for. Past months never change.">
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <Mui.Box sx={{ ml: -1.5 }}>
              <SwitchLine
                checked={form.fullSalary}
                onChange={set("fullSalary")}
                disabled={!settings.actualAllowed || !aboveCeiling}
                label={`PF on full salary (${money(member.pf_wage)} instead of ${money(Math.min(member.pf_wage, settings.ceiling))})`}
              />
            </Mui.Box>
            <Mui.Typography sx={switchHelpSx}>
              {!settings.actualAllowed
                ? "Full-salary PF is not allowed in PF settings."
                : !aboveCeiling
                  ? "Salary is within the ceiling, so PF is already on the full salary."
                  : "PF is cut on the whole Basic + DA, not just the ceiling."}
            </Mui.Typography>
            {error("fullSalary") ? <Mui.Typography sx={{ ...switchHelpSx, color: RED }}>{error("fullSalary")}</Mui.Typography> : null}
            {settings.vpfEnabled ? (
              <>
                <Mui.Box sx={{ ml: -1.5, mt: 1 }}>
                  <SwitchLine checked={form.hasExtra} onChange={set("hasExtra")} label="Extra amount every month" />
                </Mui.Box>
                <Mui.Typography sx={switchHelpSx}>A fixed amount on top of the normal PF. Enter it below.</Mui.Typography>
              </>
            ) : null}
          </Mui.Box>
          {settings.vpfEnabled ? (
            <Field label={<>Extra amount{form.hasExtra ? <Required /> : null}</>} helper={`Whole rupees. Up to ${money(maxExtra)} a month.`} error={error("extra")}>
              <PlainInput
                type="number"
                value={form.extra}
                onChange={set("extra")}
                disabled={!form.hasExtra}
                error={error("extra")}
                adornment="₹"
                placeholder={form.hasExtra ? "0" : "Turn on above"}
                inputProps={{ min: 0, step: 1, "aria-label": "Extra amount" }}
                sx={{ width: "100%" }}
              />
            </Field>
          ) : null}
          <Field label={<>From month<Required /></>} helper="First payroll month it applies to." error={error("from")} wide={!settings.vpfEnabled}>
            <SelectField value={form.from} onChange={set("from")} options={fromOptions} error={error("from")} />
          </Field>
          <Field label="Employee request" helper="Signed request letter. Optional." wide>
            <FilePick label="Upload signed letter" hint="PDF, JPG or PNG · max 5 MB" value={form.letter} onChange={set("letter")} error={error("letter")} />
          </Field>
        </FormSection>

        <FormSection title="Effect every month" info="Monthly amounts now and after this change.">
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <SimpleTable
              fit
              heads={[
                { label: "", width: 160 },
                { label: "Now", align: "right", width: 90 },
                { label: "After", align: "right", width: 90 },
              ]}
            >
              <Mui.TableRow>
                <Mui.TableCell sx={tableCellSx}>PF cut from salary</Mui.TableCell>
                <Mui.TableCell align="right" sx={tableCellSx}>{now.employee.toLocaleString("en-IN")}</Mui.TableCell>
                <Mui.TableCell align="right" sx={tableCellSx}>{after.employee.toLocaleString("en-IN")}</Mui.TableCell>
              </Mui.TableRow>
              <Mui.TableRow>
                <Mui.TableCell sx={tableCellSx}>Extra amount</Mui.TableCell>
                <Mui.TableCell align="right" sx={tableCellSx}>{now.extra.toLocaleString("en-IN")}</Mui.TableCell>
                <Mui.TableCell align="right" sx={tableCellSx}>{after.extra.toLocaleString("en-IN")}</Mui.TableCell>
              </Mui.TableRow>
              <Mui.TableRow>
                <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.heading }}>Take-home pay</Mui.TableCell>
                <Mui.TableCell sx={tableCellSx} />
                <Mui.TableCell
                  align="right"
                  sx={{ ...tableCellSx, fontWeight: designWeights.heading, color: difference > 0 ? RED : difference < 0 ? "#067647" : designColors.slate700 }}
                >
                  {difference === 0 ? "No change" : `${money(Math.abs(difference))} ${difference > 0 ? "less" : "more"}`}
                </Mui.TableCell>
              </Mui.TableRow>
              <Mui.TableRow>
                <Mui.TableCell sx={tableCellSx}>Company PF cost</Mui.TableCell>
                <Mui.TableCell align="right" sx={tableCellSx}>{now.employer.toLocaleString("en-IN")}</Mui.TableCell>
                <Mui.TableCell align="right" sx={tableCellSx}>{after.employer.toLocaleString("en-IN")}</Mui.TableCell>
              </Mui.TableRow>
            </SimpleTable>
          </Mui.Box>
        </FormSection>

        <RequestInfoBanner body="The extra goes into the employee's own PF account. PF on full salary applies to both the employee and the employer share." />
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

// ---------------- PF Opt-out (P6b) ----------------

const DOJ = "DATE_OF_JOINING";
const OPT_OUT_ERROR_KEYS = { from_type: "from", from_month: "from", reason: "reason", form11: "form11" };

// notice: why the opt-out may be refused (settings missing / turned off).
export const PfOptOutPanel = ({ open, member, notice, onClose, onSave }) => {
  const [form, setForm] = useState({ from: DOJ, reason: "", form11: null });
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [server, setServer] = useState({ fields: {}, general: "" });

  useEffect(() => {
    if (!open) return;
    setForm({ from: DOJ, reason: "", form11: null });
    setShowErrors(false);
    setSaving(false);
    setServer({ fields: {}, general: "" });
  }, [open, member]);

  if (!member) return null;
  const set = (key) => (value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setServer((current) => ({ ...current, fields: { ...current.fields, [key]: undefined } }));
  };
  const errors = {};
  if (!form.from) errors.from = "Pick when it starts";
  if (!form.reason.trim()) errors.reason = "Enter the reason";
  if (fileError(form.form11)) errors.form11 = fileError(form.form11);
  const error = (key) => server.fields[key] || (showErrors ? errors[key] : undefined);
  const options = [{ value: DOJ, label: "Date of joining" }, ...monthOptions().map((item) => ({ value: item.value, label: `From ${item.label}` }))];

  const save = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length || saving) return;
    setSaving(true);
    setServer({ fields: {}, general: "" });
    try {
      await onSave({ from: form.from, reason: form.reason.trim(), form11: form.form11 });
    } catch (saveError) {
      setServer(splitErrors(saveError, OPT_OUT_ERROR_KEYS));
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Set PF Opt-out"
      subtitle={[member.name, member.emp_id, member.department].filter(Boolean).join(" · ")}
      bodySx={panelBodySx}
      footer={<PanelActionFooter onCancel={onClose} primaryLabel={saving ? "Saving..." : "Save"} primaryProps={{ onClick: save, disabled: saving }} />}
    >
      <Mui.Stack spacing={2}>
        {server.general ? <RequestInfoBanner tone="danger" title="Couldn't save" body={server.general} /> : null}
        {notice ? <RequestInfoBanner tone="danger" body={notice} /> : null}
        <RequestInfoBanner
          tone="warning"
          title="Opt-out is usually allowed only for: new joiners, before their first payroll; PF wage above the ceiling at joining; never a PF member before (no previous UAN)."
          body="Your company decides and is responsible for this. Cauver does not check whether the employee may opt out."
        />
        <FormSection title="Opt-out Details" info="PF stops for this employee from the date below.">
          <Field wide label={<>Effective from<Required /></>} helper="Date of joining, or the first payroll month without PF." error={error("from")}>
            <SelectField value={form.from} onChange={set("from")} options={options} error={error("from")} />
          </Field>
          <Field wide label="Declaration (Form 11)" helper="Signed Form 11 from the employee. Optional.">
            <FilePick
              label="Upload declaration (Form 11)"
              hint="Optional · PDF, JPG or PNG · max 5 MB"
              value={form.form11}
              onChange={set("form11")}
              error={error("form11")}
            />
          </Field>
          <Field wide label={<>Reason<Required /></>} helper="Why the employee opts out. Kept for audit." error={error("reason")}>
            <TextField value={form.reason} onChange={set("reason")} error={error("reason")} placeholder="e.g. Employee chose not to join PF" />
          </Field>
        </FormSection>
        <RequestInfoBanner body="After saving: no PF deduction, no employer PF, not included in the ECR. HR can end the opt-out from a future month." />
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
